/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import React from 'react';
import PropTypes from 'prop-types';

// Internal dependencies
import useStyleSystem from '@rakuten/hooks/useStyleSystem';
import { View } from '@rakuten/modules-core-components';
import Label, { TYPE as LABEL_TYPE } from './../atoms/Label';
import Dialog from './Dialog';
import Disclosure from './Disclosure';


// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------

/**
 * Calculate styles for this component, optionally based on props, theme,
 * currently matching breakpoint etc.
 *
 * @param {object} args Arguments used for style calculation
 * @param {object} args.theme Current theme object
 * @return {object} Object that maps style names to style objects
 */
function createStyles({ theme }) {
  const { colors, mixins } = theme;

  return {
    detailsContainer: {
      marginTop: mixins.basicUnit(2)
    },

    details: {
      backgroundColor: colors.whiteWarm,
      borderColor: colors.divider,
      borderStyle: 'solid',
      borderRadius: mixins.basicUnit(.5),
      borderWidth: 1,
      padding: mixins.basicUnit()
    }
  };
}


// -----------------------------------------------------------------------------
// DISCLOSURE COMPONENT
// -----------------------------------------------------------------------------

function ErrorDialog(props) {
  // "Hook" this component into the theme/style system
  const [ styles ] = useStyleSystem(
    {
      id: 'ErrorDialog',
      props,
      styleFn: createStyles
    }
  );


  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <Dialog
      primaryActionButton={ props.primaryActionButton }
      secondaryActionButton={ props.secondaryActionButton }
      showCloseIcon={ false }
      title={ props.title }
    >
      { props.children }
      {
        props.details &&
          <View style={ styles.detailsContainer }>
            <Disclosure
              title="Details"
            >
              {
                React.isValidElement(props.details)
                  ? props.details
                  : <View style={ styles.details }>
                      <Label
                        texttype= { 'h2' }
                        text={ JSON.stringify(props.details) }
                        numberOfLines={ 0 }
                        type={ LABEL_TYPE.MONOSPACE }
                      />
                    </View>
              }
            </Disclosure>
          </View>
      }
    </Dialog>
  );
}


// -----------------------------------------------------------------------------
// PROPS VALIDATION
// -----------------------------------------------------------------------------

ErrorDialog.propTypes = {
  /* The main content of the error dialog [optional] */
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element)
  ]),

  /* (Technical) details of the error [optional] */
  details: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
    PropTypes.object
  ]),

  /* A button the symbolizes the primary action the user can take */
  primaryActionButton: PropTypes.element.isRequired,

  /* A button the symbolizes the secondary action the user can take [optional] */
  secondaryActionButton: PropTypes.element,

  /* The title of the eror dialog [optional] */
  title: PropTypes.string
};

ErrorDialog.defaultProps = {
  children: undefined,
  details: undefined,
  secondaryActionButton: undefined,
  title: ''
};


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default ErrorDialog;
